import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import classnames from 'classnames';
import CompareResult from '../../components/CompareResult';
import AddEntities from '@/pages/intelligence/pages/Compare/components/AddEntities';
import ConfirmModal from '../../components/ConfirmModal';
import DownloadModal from '../DownloadModal';
import DefaultEntityRenderer from './renderers/EntityRenderer';
import useCompareTables from '../../hooks/useCompareTables';
import useCompareTrends from '../../hooks/useCompareTrends';
import useAddGames from '../../hooks/useAddGames';
import useDeleteGames from '../../hooks/useDeleteGames';
import useDownload from '../../hooks/useDownload';
import useUrlGamesParam from '@/pages/intelligence/pages/Compare/hooks/useUrlGamesParam';
import { fetchKeywordsPlus, fetchGamesByKeywordPlus, fetchSimilarGamesPlus, addComparingGamesPlus } from '../../services';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { ComposedDateMode } from '@/pages/intelligence/components/ComposedDatePicker/enums';
import moment from 'moment';
import type { IDateType, IDateValue } from '@/pages/intelligence/types/common';
import type { IPCConsoleGame } from '@/pages/intelligence/types/pcConsoleCompare';
import type { IComposedDateData } from '@/pages/intelligence/components/ComposedDatePicker/types';
import type { ICompareCoreProps, ICompareCoreInstance } from './types';

function CompareCore(props: ICompareCoreProps, ref: React.Ref<ICompareCoreInstance>) {
  const {
    filter,
    pageKey,
    pageName,
    entityTypes,
    periods,
    devices,
    dateType,
    dateTypes,
    visualisTooltipTitle,
    pageLogKey,
    isOnlySingleMetricLine,
    isShowTitleTips,
    titleTips,
    headerColumnRenderer,
    fieldsMappings,
    entityRenderer: entityRendererOutter,
    style,
    className,
    isReset,
    setIsReset,
    rangePickerProps,
  } = props;
  const rangeTimeComponentDefault = rangePickerProps?.defaultValue;
  const { formatMessage } = useIntl();

  const { gameKey = 'gameId' } = fieldsMappings || {};

  const entityRenderer = typeof entityRendererOutter === 'function' ? entityRendererOutter : DefaultEntityRenderer;

  const clearAllGamesMsg = formatMessage({ id: 'dbi_compare_clear_all_confirm_tips' });

  // 下载需要用到的时间，参数最外层的start_date、end_date
  const [downloadDateValue, setDownloadDateValue] = useState<IDateValue>(['', '']);

  /** 组合时间筛选器部分 **/
  const [composedDateData, setComposedDateData] = useState<IComposedDateData>({
    mode: ComposedDateMode.Absolute,
    dateType,
    dateValue: [],
  });
  const handleComposeDateChange = (mode: ComposedDateMode, dateType: IDateType, dateValue: IDateValue) => {
    const composedDateDataTemp = {
      ...composedDateData,
      mode,
      dateType,
      dateValue,
    };
    if (mode === ComposedDateMode.Absolute) {
      setDownloadDateValue(dateValue);
    }
    setComposedDateData(composedDateDataTemp);
    updateCompareTrendsData({ composedDateData: composedDateDataTemp });
    if(setIsReset instanceof Function){
      setIsReset(false)
    }
  };
  /** 组合时间筛选器部分 **/

  /** 对比表格部分 **/
  const {
    isLoading: isTablesLoading,
    setIsLoading: setIsTablesLoading,
    compareTableData,
    updateCompareTableData,
    onDeleteGames: handleDeleteGamesInTablesLogic,
  } = useCompareTables({
    entityTypes,
    periods,
    devices,
    filter,
  });
  /** 对比表格部分 **/

  /** 对比折线图、指标聚合值展示表格部分 **/
  const {
    isLoading: isVisualisLoading,
    isNoData: isVisualisNoData,
    compareTrendsData,
    updateCompareTrendsData,
    onDeleteGames: handleDeleteGamesInTrendsLogic,
  } = useCompareTrends({
    entityTypes,
    devices,
    games: compareTableData?.games,
    filter,
    composedDateData,
    setComposedDateData,
    setDownloadDateValue,
  });
  /** 对比折线图、指标聚合值展示表格部分 **/

  /** 添加游戏部分 **/
  const {
    visible: isAddModalVisible,
    onAddClick: handleAddGamesClick,
    onAddConfirm: handleAddGamesConfirm,
    onAddClose: handleAddGamesClose,
  } = useAddGames({
    updateCompareTableData,
    updateCompareTrendsData,
  });
  /** 添加游戏部分 **/

  /** URL games parameter handling - auto-add games from ?games=id1,id2,... **/
  const existingGameIds = useMemo(() => {
    return (compareTableData?.games || []).map((game) => game?.gameId).filter(Boolean);
  }, [compareTableData?.games]);

  const handleUrlGamesAdded = useCallback((games: any[]) => {
    // Refresh compare table and trends data with the newly added games
    updateCompareTableData({ games });
    updateCompareTrendsData({ games });
  }, [updateCompareTableData, updateCompareTrendsData]);

  useUrlGamesParam({
    entityType: 'pc,console',
    existingGameIds,
    addComparingGames: addComparingGamesPlus,
    onGamesAdded: handleUrlGamesAdded,
  });
  /** URL games parameter handling **/

  /** 删除游戏部分，包含 单个游戏删除，全部游戏删除 **/
  const {
    visible: isClearAllModalVisile,
    onDelete: handleDeleteGameClick,
    onClearAllClick: handleClearAllGamesClick,
    onClearAllConfirm: handleClearAllGamesConfirm,
    onClearAllCancel: handleClearAllGamesCancel,
    onClearAllClose: handleClearAllGamesClose,
  } = useDeleteGames({
    setIsLoading: setIsTablesLoading,
    games: compareTableData?.games,
  });

  const handleDeleteGamesPlus = async (game: IPCConsoleGame) => {
    handleDeleteGamesInTablesLogic([game?.gameId]);
    handleDeleteGamesInTrendsLogic([game?.gameId]);
    return await handleDeleteGameClick(game);
  };

  const handleClearAllGamesConfirmPlus = async () => {
    const gamesIds = compareTableData?.games?.map((game) => game?.gameId) || [];
    handleDeleteGamesInTablesLogic(gamesIds);
    handleDeleteGamesInTrendsLogic(gamesIds);
    return await handleClearAllGamesConfirm();
  };
  /** 删除游戏部分，包含 单个游戏删除，全部游戏删除 **/

  const {
    visible: isDownloadVisible,
    setVisible: setIsDownloadVisible,
    isDownloading,
    handleConfirm: handleDownloadConfirm,
    handleCancel: handleDownloadCancel,
    handleClose: handleDownloadClose,
  } = useDownload({
    pageName,
    games: compareTableData?.games,
    devices: filter?.devices,
    composedDateData,
    filter,
    downloadDateValue,
  });

  useImperativeHandle(ref, () => ({
    updateCompareTableData,
    updateCompareTrendsData,
  }));

  /** 页面打点 **/
  useEffect(() => {
    // @ts-ignore
    const pageLogConfig = dbiPageLogMes[pageLogKey];
    if (pageLogConfig) {
      buriedPointPage(pageLogConfig);
    }
  }, []);
  /** 页面打点 **/

  useEffect(()=>{
    if(isReset){
      setComposedDateData({
        mode: ComposedDateMode.Absolute,
        dateType,
      dateValue: [
        moment(rangeTimeComponentDefault?.[dateType]?.[0])?.format('YYYY-MM-DD'),
        moment(rangeTimeComponentDefault?.[dateType]?.[1])?.format('YYYY-MM-DD')
      ] || []
      })
    }
  },[isReset])

  return (
    <div
      style={style}
      className={classnames('dbi-pc-console-compare_compare-core', className, {
        'is-visualis-no-data': isVisualisNoData,
      })}
    >
      {/* 筛选器 */}
      <CompareResult
        pageKey={pageKey}
        isLoading={isTablesLoading}
        isVisualisLoading={isVisualisLoading}
        isVisualisNoData={isVisualisNoData}
        isOnlySingleMetricLine={isOnlySingleMetricLine}
        isShowTitleTips={isShowTitleTips}
        titleTips={titleTips}
        fieldsMappings={fieldsMappings}
        isDownloading={isDownloading}
        setIsDownloadVisible={setIsDownloadVisible}
        isHeatMap={filter?.isHeatMap}
        isShowTopCountries={filter?.isShowTop3Countries}
        periods={periods}
        metrics={filter?.metrics}
        dateTypes={dateTypes}
        devices={filter?.devices}
        composedDateData={composedDateData}
        rangePickerProps={rangePickerProps}
        oncomposedDateDataChange={handleComposeDateChange}
        games={compareTableData?.games}
        headerColumnRenderer={headerColumnRenderer}
        tablesProps={{
          tablesMetricsMap: compareTableData?.tablesMetricsMap,
          tablesPeriodMaxMetricsMap: compareTableData?.tablesPeriodMaxMetricsMap,
          onAddGames: handleAddGamesClick,
          onDeleteGames: handleDeleteGamesPlus,
          onClearAllGames: handleClearAllGamesClick,
        }}
        trendsProps={{
          tooltipTitle: visualisTooltipTitle,
          trendsDimensions: compareTrendsData?.trendsDimensions,
          trendsXAxisData: compareTrendsData?.trendsXAxisData,
          trendsData: compareTrendsData?.trendsData,
          trendsCumulativeMetricsDataMap: compareTrendsData?.trendsCumulativeMetricsDataMap,
        }}
      />
      {/* 添加游戏弹窗 */}
      <AddEntities
        entityKey={gameKey}
        entities={compareTableData?.games}
        visible={isAddModalVisible}
        entityRenderer={entityRenderer}
        keywordsFetcher={fetchKeywordsPlus}
        entitiesFetcher={fetchGamesByKeywordPlus}
        similarEntitiesFetcher={fetchSimilarGamesPlus}
        onConfirm={handleAddGamesConfirm}
        onClose={handleAddGamesClose}
      />
      {/* clear all 二次确认弹窗 */}
      <ConfirmModal
        message={clearAllGamesMsg}
        visible={isClearAllModalVisile}
        onConfirm={handleClearAllGamesConfirmPlus}
        onCancel={handleClearAllGamesCancel}
        onClose={handleClearAllGamesClose}
      />
      {/* 下载弹窗 */}
      <DownloadModal
        downloadLogConfigKey={`${pageLogKey}DownloadExcel`}
        defaultDateTypes={[composedDateData?.dateType]}
        visible={isDownloadVisible}
        setVisible={setIsDownloadVisible}
        onConfirm={handleDownloadConfirm}
        onCancel={handleDownloadCancel}
        onClose={handleDownloadClose}
      />
    </div>
  );
}

export default forwardRef(CompareCore);
